// "Show more" pages for the /events, /tours and /boats hubs, and the filtered
// /tours and /boats hubs (see the bottom of this file).
//
// The first HUB_PAGE_SIZE rows of each hub are baked at build time (see
// the app's list-prefetch.ts); these fetch the rows after them in the browser, from the
// very RPC that produced the seed, paged with PostgREST's .range() (LIMIT /
// OFFSET applied over the function's result). Offset paging needs both sides
// to agree on a total order: event_shows orders by (start_ts, id),
// tours_by_city by tour_rank_score and boats_by_city by rating, both ending
// in id (20261006120000_deterministic_list_order.sql,
// 20261006130000_tour_rank_by_reviews.sql).
//
// Only the fields the cards render are requested — the same shape the seed
// trimmers in the app's list-prefetch.ts produce — so a page of tours or boats is a few
// KB rather than the full rows with `raw` and every locale's description.

import { SUPPORTED_LOCALES, type Locale } from './i18n/locales';
import type { Boat, BoatTranslations } from './boats';
import {
  boatFilterArgs,
  eventFilterArgs,
  tourFilterArgs,
  type BoatFilters,
  type EventFilters,
  type TourFilters,
} from './hub-filters';
import {
  EVENTS_INDEX_QUERY,
  EVENTS_RPC,
  boatRentalCityQuery,
  boatsRpcArgs,
  eventsRpcArgs,
} from './list-queries';
import { supabase, type DbEvent, type LocaleCopy } from './supabase';
import type { Tour, TourTranslations } from './tours';

/** Fetches `count` rows starting at `offset`. */
export type FetchPage<T> = (offset: number, count: number) => Promise<T[]>;

// Each locale's title only, aliased per locale: `en:translations->en->>title`.
const TITLE_COLUMNS = SUPPORTED_LOCALES.map((l) => `${l}:translations->${l}->>title`).join(',');

type TitleColumns = Partial<Record<Locale, string | null>>;

function titleTranslations(row: TitleColumns) {
  const out: Partial<Record<Locale, LocaleCopy>> = {};
  for (const locale of SUPPORTED_LOCALES) {
    const title = row[locale];
    if (title) out[locale] = { title, description: '', summary: '' };
  }
  return out;
}

/**
 * Events after the seed. `anchor` is the first seeded show's start_ts: the
 * seed's window began at build time, and anchoring there (rather than at
 * "now") keeps shows that have started since the build from shifting every
 * offset — no show has a first date between build time and the first seeded
 * one, so both windows list the same shows in the same order.
 */
export function eventsPageFetcher(anchor: string | undefined): FetchPage<DbEvent> {
  return async (offset, count) => {
    const from = anchor ? new Date(anchor) : new Date();
    const { data, error } = await supabase
      .rpc(EVENTS_RPC, eventsRpcArgs({ ...EVENTS_INDEX_QUERY, from, limit: offset + count }))
      .range(offset, offset + count - 1);
    if (error) throw new Error(error.message);
    return (data ?? []) as DbEvent[];
  };
}

const TOUR_CARD_COLUMNS = `id,source,title,city_id,rating,review_count,duration,from_price,currency,image_url,${TITLE_COLUMNS}`;

type TourCardRow = Omit<Tour, 'translations'> & TitleColumns;

function toTourCard(row: TourCardRow): Tour {
  return {
    id: row.id,
    source: row.source,
    source_id: '',
    title: row.title,
    description: null,
    city_id: row.city_id,
    rating: row.rating,
    review_count: row.review_count,
    duration: row.duration,
    from_price: row.from_price,
    currency: row.currency,
    image_url: row.image_url,
    booking_url: null,
    delisted_at: null,
    translations: titleTranslations(row) as TourTranslations,
  };
}

export const fetchToursPage: FetchPage<Tour> = async (offset, count) => {
  const { data, error } = await supabase
    .rpc('tours_by_city', { p_city: null, p_limit: offset + count })
    .select(TOUR_CARD_COLUMNS)
    .range(offset, offset + count - 1);
  if (error) throw new Error(error.message);
  return ((data ?? []) as unknown as TourCardRow[]).map(toTourCard);
};

// `year` feeds the "newest boats" sort of the city pages' client-side filter.
const BOAT_CARD_COLUMNS = `id,source,name,boat_type,city_id,rating,year,from_price,currency,image_url,title_suffix:translations->_title_suffix,${TITLE_COLUMNS}`;

type BoatCardRow = Omit<Boat, 'translations'> &
  TitleColumns & { title_suffix: BoatTranslations['_title_suffix'] | null };

function toBoatCard(row: BoatCardRow): Boat {
  return {
    id: row.id,
    source: row.source,
    source_id: '',
    name: row.name,
    boat_type: row.boat_type,
    city_id: row.city_id,
    rating: row.rating,
    year: row.year,
    owner_type: null,
    from_price: row.from_price,
    currency: row.currency,
    image_url: row.image_url,
    booking_url: null,
    has_owner_text: false,
    delisted_at: null,
    translations: {
      ...titleTranslations(row),
      ...(row.title_suffix ? { _title_suffix: row.title_suffix } : {}),
    } as BoatTranslations,
  };
}

export const fetchBoatsPage: FetchPage<Boat> = async (offset, count) => {
  const { data, error } = await supabase
    .rpc('boats_by_city', { p_city: null, p_limit: offset + count })
    .select(BOAT_CARD_COLUMNS)
    .range(offset, offset + count - 1);
  if (error) throw new Error(error.message);
  return ((data ?? []) as unknown as BoatCardRow[]).map(toBoatCard);
};

// ---------------------------------------------------------------------------
// Filtered hubs. A hub with any filter or sort set reads tours_filtered /
// boats_filtered instead of its seed's RPC — first page included, since the
// seed is the unfiltered list. Those RPCs take no limit: .range() alone pages
// them, so `withTotal` (PostgREST's exact count) is the true number of matches.
// ---------------------------------------------------------------------------

/** One page of a filtered hub; `total` only when asked for, else null. */
export type FilteredPage<T> = { rows: T[]; total: number | null };

export type FetchFiltered<T, F> = (
  filters: F,
  offset: number,
  count: number,
  withTotal: boolean,
) => Promise<FilteredPage<T>>;

export const fetchFilteredTours: FetchFiltered<Tour, TourFilters> = async (
  filters,
  offset,
  count,
  withTotal,
) => {
  const { data, error, count: total } = await supabase
    .rpc('tours_filtered', tourFilterArgs(filters), withTotal ? { count: 'exact' } : undefined)
    .select(TOUR_CARD_COLUMNS)
    .range(offset, offset + count - 1);
  if (error) throw new Error(error.message);
  return {
    rows: ((data ?? []) as unknown as TourCardRow[]).map(toTourCard),
    total: total ?? null,
  };
};

export const fetchFilteredBoats: FetchFiltered<Boat, BoatFilters> = async (
  filters,
  offset,
  count,
  withTotal,
) => {
  const { data, error, count: total } = await supabase
    .rpc('boats_filtered', boatFilterArgs(filters), withTotal ? { count: 'exact' } : undefined)
    .select(BOAT_CARD_COLUMNS)
    .range(offset, offset + count - 1);
  if (error) throw new Error(error.message);
  return {
    rows: ((data ?? []) as unknown as BoatCardRow[]).map(toBoatCard),
    total: total ?? null,
  };
};

/**
 * Filtered shows for the /events hub (`city` null) or one /[city]/events page,
 * from event_shows_filtered — card fields only, each locale's title alone in
 * `translations`. `anyUntilDays` is how far ahead the unfiltered list reaches
 * (eventFilterArgs).
 */
export function filteredEventsFetcher(
  city: string | null,
  anyUntilDays: number,
): FetchFiltered<DbEvent, EventFilters> {
  return async (filters, offset, count, withTotal) => {
    const { data, error, count: total } = await supabase
      .rpc(
        'event_shows_filtered',
        eventFilterArgs(filters, city, anyUntilDays),
        withTotal ? { count: 'exact' } : undefined,
      )
      .range(offset, offset + count - 1);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as Omit<DbEvent, 'source_id' | 'description' | 'performer' | 'address' | 'ticket_url'>[];
    return {
      rows: rows.map((row) => ({
        ...row,
        source_id: '',
        description: null,
        performer: null,
        address: null,
        ticket_url: null,
      })),
      total: total ?? null,
    };
  };
}

/**
 * Every boat in one city, as cards — for the /boat-rental/[city] page's
 * filters, which then filter and sort in the browser (applyBoatFilters). The
 * page's own seed can't serve them: past the top cards it drops image_url and
 * year to keep the static HTML small. Same RPC and order as that seed.
 */
export async function fetchCityBoatCards(cityId: string): Promise<Boat[]> {
  const { data, error } = await supabase
    .rpc('boats_by_city', boatsRpcArgs(boatRentalCityQuery(cityId)))
    .select(BOAT_CARD_COLUMNS);
  if (error) throw new Error(error.message);
  return ((data ?? []) as unknown as BoatCardRow[]).map(toBoatCard);
}
